import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ComponentModel } from 'src/app/models/maintenance/component.model';
import { ComponentService } from 'src/app/services/maintenance/component.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-component',
  templateUrl: './register-component.component.html',
  styleUrls: ['./register-component.component.scss'],
  standalone: false
})
export class RegisterComponentComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly componentService = inject(ComponentService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastHandlerService = inject(ToastHandlerService);

  componentContract: ComponentModel = new ComponentModel();
  componentId: number = 0;
  formComponent: FormGroup = new FormGroup({});
  maintenanceRoutineId: number = 0;
  suscription: Subscription;


  ngOnInit() {
    this.validateForm(this.componentContract);
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.componentContract = inputs.componentModel;
      this.maintenanceRoutineId = inputs.maintenanceRoutineId;
    });
    this.componentId = this.componentContract.componentId;
    this.handleComponent();
  }

  async changeCode(code: number) {
    if (!code || this.componentContract.code === code) { return; }
    try {
      const response = await firstValueFrom(this.componentService.getComponentByCode(this.maintenanceRoutineId, code));
      this.componentId = response.componentId;
      this.componentContract = response;
      this.validateForm(this.componentContract);
    } catch (error) {
      console.error('Error al buscar el componente por código.', error);
    }
  }

  createNewRegister() {
    this.componentId = 0;
    this.componentContract = new ComponentModel();
    this.componentContract.maintenanceRoutineId = this.maintenanceRoutineId;
    this.validateForm(this.componentContract);
  }

  async getComponentById() {
    try {
      const response = await firstValueFrom(this.componentService.getComponentById(this.componentId));
      this.componentContract = response;
      this.validateForm(response);
    } catch (error) {
      console.error('Error al obtener el componente por id.', error);
    }
  }

  async handleComponent() {
    if (this.componentId !== null && this.componentId !== 0) {
      await this.getComponentById();
    } else {
      this.createNewRegister();
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  async send() {
    this.validateFormControls();
    if (this.formComponent.valid) {
      const modelComponent = this.formComponent.getRawValue() as ComponentModel;
      if (this.componentId == 0) {
        try {
          const response = await firstValueFrom(this.componentService.createComponent(modelComponent));
          this.componentId = response.componentId;
          this.componentContract = response;
          this.validateForm(this.componentContract);
          this.toastHandlerService.showToast('success', `El componente fue creado exitosamente.`);
        } catch (error) {
          console.error('Error al crear el componente.', error);
        }
      } else {
        this.componentContract.name = modelComponent.name;
        try {
          const response = await firstValueFrom(this.componentService.updateComponent(this.componentContract));
          this.componentContract = response;
          this.validateForm(this.componentContract);
          this.toastHandlerService.showToast('success', `El componente fue modificado exitosamente.`);
        } catch (error) {
          console.error('Error al actualizar el componente.', error);
        }
      }
      this.onReloadEmit();
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      name: {
        required: 'El campo nombre es obligatorio.',
        maxlength: 'El campo nombre no puede tener más de 80 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formComponent.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(component: ComponentModel) {
    this.formComponent = this.formBuilder.group({
      maintenanceRoutineId: [this.maintenanceRoutineId],
      code: [{ value: component.code, disabled: this.componentId === 0 }, [Validators.required]],
      name: [component.name, [Validators.required, Validators.maxLength(80)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formComponent.controls).forEach((key) => {
      const control = this.formComponent.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
